import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const MembershipStatusCard = ({ membershipTier = "Explorador", expirationDate = new Date(Date.now() + 15 * 24 * 60 * 60 * 1000) }) => {
  const navigate = useNavigate();
  const [daysRemaining, setDaysRemaining] = useState(0);

  useEffect(() => {
    const calculateDaysRemaining = () => {
      const now = new Date();
      const expiration = new Date(expirationDate);
      const diffTime = expiration - now;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      setDaysRemaining(diffDays > 0 ? diffDays : 0);
    };

    calculateDaysRemaining();
    const interval = setInterval(calculateDaysRemaining, 3600000);
    return () => clearInterval(interval);
  }, [expirationDate]);

  const tierConfig = {
    'Explorador': {
      icon: 'Compass',
      color: 'var(--color-muted-foreground)',
      bgColor: 'bg-muted',
      price: '$20.000',
      benefits: [
        'Acceso a la plataforma',
        'Escaneo QR',
        '500 Notificaciones',
        'Historial de descuentos',
        'Soporte por Email',
        '15 descuentos/mes'
      ]
    },
    'Emprendedor': {
      icon: 'TrendingUp',
      color: 'var(--color-accent)',
      bgColor: 'bg-accent/10',
      price: '$50.000',
      benefits: [
        'Acceso a la plataforma',
        'Escaneo QR',
        '2000 Notificaciones',
        'Historial de descuentos',
        'Programa Bizznet Rewards',
        'Soporte prioritario',
        '50 descuentos/mes'
      ]
    },
    'Elite': {
      icon: 'Crown',
      color: 'var(--color-primary)',
      bgColor: 'bg-primary/10',
      price: '$70.000',
      benefits: [
        'Acceso a la plataforma',
        'Notificaciones ilimitadas',
        'Escaneo QR',
        'Programa Bizznet Rewards',
        'Historial de descuentos',
        'Soporte 24/7 dedicado',
        'Descuentos ilimitados'
      ]
    }
  };

  const currentTier = tierConfig?.[membershipTier] || tierConfig?.['Explorador'];
  const isExpiringSoon = daysRemaining <= 7;

  const handleRenew = () => {
    navigate('/landing-page');
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6 shadow-card">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className={`flex items-center justify-center w-12 h-12 rounded-lg ${currentTier?.bgColor}`}>
            <Icon name={currentTier?.icon} size={24} color={currentTier?.color} />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">Membresía {membershipTier}</h2>
            <p className="text-sm text-muted-foreground">{currentTier?.price} COP/mes</p>
          </div>
        </div>
        {isExpiringSoon && (
          <div className="px-3 py-1 bg-warning/10 rounded-full">
            <span className="text-xs font-bold text-warning">¡Expira Pronto!</span>
          </div>
        )}
      </div>
      <div className="mb-6 p-4 bg-muted rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-foreground">Días Restantes</span>
          <span className="text-2xl font-bold" style={{ color: isExpiringSoon ? 'var(--color-warning)' : 'var(--color-success)' }}>
            {daysRemaining}
          </span>
        </div>
        <div className="w-full h-2 bg-background rounded-full overflow-hidden">
          <div
            className="h-full transition-all duration-500"
            style={{
              width: `${(daysRemaining / 30) * 100}%`,
              backgroundColor: isExpiringSoon ? 'var(--color-warning)' : 'var(--color-success)'
            }}
          />
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          Vence el {new Date(expirationDate)?.toLocaleDateString('es-CO', { day: '2-digit', month: 'long', year: 'numeric' })}
        </p>
      </div>
      <div className="mb-6">
        <h3 className="text-sm font-semibold text-foreground mb-3">Beneficios Activos</h3>
        <div className="space-y-2">
          {currentTier?.benefits?.map((benefit, index) => (
            <div key={index} className="flex items-center gap-2">
              <div className="flex items-center justify-center w-5 h-5 rounded-full bg-success/10">
                <Icon name="Check" size={14} color="var(--color-success)" />
              </div>
              <span className="text-sm text-foreground">{benefit}</span>
            </div>
          ))}
        </div>
      </div>
      {isExpiringSoon && (
        <Button
          variant="default"
          fullWidth
          onClick={handleRenew}
          iconName="RefreshCw"
          iconPosition="left"
        >
          Renovar Membresía
        </Button>
      )}
    </div>
  );
};

export default MembershipStatusCard;